import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, Optional} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Ast, ExpressionCompilerService} from '@ngmedax/expression-compiler';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {Subscription} from 'rxjs';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {QuestionnaireStateService} from '../../../services/questionnaire-state.service';
import {QuestionnaireConditionService} from '../../../services/questionnaire-condition.service';
import {DependencyCatalogService} from '../../dependency-editor/dependency-catalog.service';
import {DependencyEditorComponent} from '../../dependency-editor/dependency-editor.component';
import {applyDependency, dependencyIssue, dependencySummary, DependencySource, readDependency} from '../../dependency-editor/dependency-conditions';

export interface ConditionsComponent extends Translatable {}

/** Summarizes both legacy and advanced dependencies without changing their stored representation on viewing. */
@Component({
  selector: 'app-qa-conditions', changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './conditions.component.html', styleUrls: ['./conditions.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class ConditionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() question: Questionnaire.Container;
  @Input() locale = 'de_DE';
  public summary = '';
  public warning = '';
  public active = false;
  public group: Ast.Group;
  public sources: DependencySource[] = [];
  private subscription: Subscription;
  private modal: NgbModalRef;

  /** Injects the shared catalog, compiler and questionnaire change notifications. */
  public constructor(private modals: NgbModal, private compiler: ExpressionCompilerService,
    private catalog: DependencyCatalogService, private conditions: QuestionnaireConditionService,
    private state: QuestionnaireStateService, private ref: ChangeDetectorRef,
    @Optional() private translationService: TranslationService) {}

  /** Updates the summary when answer titles or available variables change. */
  public ngOnInit(): void {
    this.refresh();
    this.subscription = this.conditions.onUpdateSelect.subscribe(() => this.refresh());
  }

  /** Rebuilds the summary when selecting another question or content language. */
  public ngOnChanges(): void { this.refresh(); }

  /** Prevents a pending dialog from applying to a removed inspector. */
  public ngOnDestroy(): void { this.subscription?.unsubscribe(); this.modal?.dismiss(); }

  /** Reads the effective condition and reports invalid references without removing them. */
  private refresh(): void {
    if (!this.question) { return; }
    const sources = this.catalog.build(this.locale);
    this.sources = sources;
    try { this.group = readDependency(this.question.conditions, this.compiler); }
    catch (error) { this.group = null; }
    this.active = !this.group || !!this.group.elements.length;
    this.warning = dependencyIssue(this.group, sources, this.compiler);
    this.summary = dependencySummary(this.group, sources, text => this._(text));
    this.ref.markForCheck();
  }

  /** Opens a detached draft and publishes only explicitly applied changes. */
  public edit(): void {
    if (this.modal) { return; }
    const question = this.question;
    const original = JSON.stringify(question.conditions || {});
    this.refresh();
    const initial = JSON.stringify(this.group);
    const modal = this.modals.open(DependencyEditorComponent, {
      centered: true, size: 'lg', windowClass: 'qa-template-condition-modal', ariaLabelledBy: 'qa-dependency-title'
    });
    this.modal = modal;
    Object.assign(modal.componentInstance, {
      group: this.group, locale: this.locale, existing: this.active, originalSource: question.conditions?.expression || ''
    });
    modal.result.then((group: Ast.Group) => {
      this.modal = null;
      if (JSON.stringify(group) === initial) { return; }
      if (question !== this.question || JSON.stringify(question.conditions || {}) !== original) {
        this.refresh();
        this.warning = 'Die Bedingungen wurden zwischenzeitlich geändert. Bitte öffnen Sie den Editor erneut.';
        return;
      }
      question.conditions = applyDependency(question.conditions, group, this.compiler);
      const position = this.state.getQuestionnaire().questions.indexOf(question);
      this.state.updateElement(question, position);
      this.refresh();
    }, () => { this.modal = null; this.refresh(); });
  }
}
